import { useQuery } from '@apollo/client'
import {
  ChartLine,
  Logs,
  Scan,
  Settings,
  Lock,
  Rocket,
  Globe,
  Braces,
  Waypoints,
} from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/sv/sidebar'
import { useCompanyPath, useIdCompany, useRBAC } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { STATIC_SITE_DISPLAY_NAME_QUERY } from '@/routes/static-site/queries'
import type {
  StaticSiteDisplayNameQuery,
  StaticSiteDisplayNameQueryVariables,
} from '@/routes/static-site/queries.types.gen'

const items = [
  {
    title: 'Overview',
    url: '/static-site/:staticSiteName/overview',
    icon: Scan,
    activeTags: ['/overview'],
  },
  {
    title: 'Deployments',
    url: '/static-site/:staticSiteName/deployments',
    icon: Rocket,
    activeTags: ['/deployment'],
  },
  {
    title: 'Logs',
    url: '/static-site/:staticSiteName/logs',
    icon: Logs,
    activeTags: ['/logs'],
  },
  {
    title: 'Analytics',
    url: '/static-site/:staticSiteName/analytics',
    icon: ChartLine,
    activeTags: ['/analytics'],
  },
  {
    title: 'Environment variables',
    url: '/static-site/:staticSiteName/environment-variables',
    icon: Braces,
    activeTags: ['/environment-variables'],
  },
  {
    title: 'Domains',
    url: '/static-site/:staticSiteName/domains',
    icon: Globe,
    activeTags: ['/domains'],
  },
  {
    title: 'Networking',
    url: '/static-site/:staticSiteName/networking',
    icon: Waypoints,
    activeTags: ['/networking'],
  },
  {
    title: 'Settings',
    url: '/static-site/:staticSiteName/settings',
    icon: Settings,
    activeTags: ['/settings'],
  },
] as const

export const StaticSiteNav = () => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const location = useLocation()
  const { getCompanyPath } = useCompanyPath()
  const { staticSiteName = '' } = useParams<{ staticSiteName: string }>()
  const { data } = useQuery<
    StaticSiteDisplayNameQuery,
    StaticSiteDisplayNameQueryVariables
  >(STATIC_SITE_DISPLAY_NAME_QUERY, {
    variables: { staticSiteName },
    fetchPolicy: 'cache-only',
  })

  const idStaticSite = useMemo(() => data?.staticSite?.id ?? '', [data])
  const idProjectStaticSite = useMemo(
    () => data?.staticSite?.idProject ?? '',
    [data]
  )
  const hasAccess = useMemo(
    () =>
      data === undefined
        ? true
        : rbac
            .company(idCompany)
            .project(idProjectStaticSite)
            .staticSite(idStaticSite)
            .view.can(),
    [rbac, idCompany, idStaticSite, idProjectStaticSite, data]
  )

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const isActive = item.activeTags.some((tag) =>
              location.pathname.includes(tag)
            )
            return (
              <SidebarMenuItem
                key={item.url}
                title={item.title}
                className={cn(
                  !hasAccess
                    ? 'opacity-50 cursor-not-allowed pointer-events-none'
                    : undefined
                )}
              >
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  disabled={!hasAccess}
                >
                  {hasAccess ? (
                    <Link
                      to={getCompanyPath({
                        pathname: item.url,
                        variables: { staticSiteName },
                      })}
                    >
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Lock />
                      <span>{item.title}</span>
                    </div>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
